import React, { useState, useEffect, useCallback, useRef } from 'react';

import { useHistory } from 'react-router-dom';
import { FaUsers } from 'react-icons/fa';
import { MdSearch } from 'react-icons/md';
import { IPaginate } from 'shared/interfaces';
import { useToast } from 'contexts';
import { ModalHandles } from 'components/Modal';

import { getAllFornecedores, deleteFornecedor } from 'services/api/fornecedor';

import * as C from 'components';
import * as S from './styles';

import { IFornecedor } from '../interfaces';

export const FornecedorList: React.FC = () => {
  const { addToast } = useToast();
  const history = useHistory();
  const [fornecedores, setFornecedores] = useState<IPaginate<IFornecedor>>();
  const [fornecedorAtual, setFornecedorAtual] = useState<IFornecedor>();
  const [loading, setLoading] = useState(false);
  const [term, setTerm] = useState('');
  const modalRemoverRef = useRef<ModalHandles>(null);

  const handleSearch = useCallback(
    ({ page, per_page }) => {
      setLoading(true);

      const params = {
        page,
        per_page,
        term,
      };

      getAllFornecedores(params)
        .then((data) => {
          setFornecedores(data);

          setLoading(false);
        })
        .catch((e) => {
          addToast({
            type: 'error',
            title: 'Ops, Erro',
            description: e.response?.data?.message,
          });

          setLoading(false);
        });
    },
    [addToast, term],
  );

  useEffect(() => {
    const params = {
      page: 1,
      per_page: 20,
    };

    handleSearch(params);
  }, [handleSearch]);

  const handleRemove = async (): Promise<void> => {
    try {
      await deleteFornecedor(fornecedorAtual!.id);

      if (!fornecedores) return;

      const newList = fornecedores.data.filter(
        (item) => item.id !== fornecedorAtual?.id,
      );
      setFornecedores({ ...fornecedores, data: newList });

      modalRemoverRef.current?.closeModal();

      addToast({
        type: 'success',
        title: 'Sucesso',
        description: 'Registro removido com sucesso',
      });
    } catch (error) {
      addToast({
        type: 'error',
        title: 'Ops, Erro',
        description: 'Ocorreu um erro ao remover o registro',
      });
    }
  };

  const handleOpenModalRemover = useCallback(
    (fornecedorSelected: IFornecedor): void => {
      setFornecedorAtual(fornecedorSelected);
      modalRemoverRef.current?.openModal();
    },
    [],
  );

  return (
    <S.Container>
      <C.HeaderBackground>
        <C.HeaderPage>
          <C.HeaderInfo title="Fornecedores" icon={FaUsers} />

          <S.ContainerSearch>
            <C.InputSearch
              title="Digite aqui o que deseja pesquisar"
              icon={MdSearch}
              placeholder="Pesquisar"
              onChange={(e) => setTerm(e.target.value)}
              onKeyDown={(e) => {
                e.key === 'Enter'
                  ? handleSearch({ page: 1, per_page: fornecedores?.per_page })
                  : {};
              }}
            />
          </S.ContainerSearch>

          <div>
            <C.Can checkPermission="fornecedores_create">
              <C.ButtonLink title="Adicionar Cliente" to="/fornecedores/new">
                Adicionar
              </C.ButtonLink>
            </C.Can>
          </div>
        </C.HeaderPage>
      </C.HeaderBackground>

      <S.Panel>
        {loading ? (
          <C.Loader loading={loading} align="center" message="Carregando..." />
        ) : (
          <>
            <div className="tbl-content">
              <C.Table>
                <thead>
                  <tr>
                    <th className="text-left">Descrição</th>
                    <th className="text-left">Tipo Fornecedor</th>
                    <th className="text-center">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {fornecedores &&
                    fornecedores.data.map((item: IFornecedor) => (
                      <tr key={item.id}>
                        <td>{item.nome}</td>
                        <td>{item.tipo_fornecedor}</td>
                        <td className="text-center">
                          <C.PopUpMenu>
                            <C.Can checkPermission="fornecedores_update">
                              <button
                                type="button"
                                onClick={() =>
                                  history.push(`/fornecedores/edit/${item.id}`)
                                }
                              >
                                <S.IconEdit
                                  title="Editar"
                                  size={24}
                                  color="#707070"
                                />
                                Editar
                              </button>
                            </C.Can>

                            <C.Can checkPermission="fornecedores_delete">
                              <button
                                type="button"
                                onClick={() => handleOpenModalRemover(item)}
                              >
                                <S.IconDelete
                                  title="Remover"
                                  size={24}
                                  color="#707070"
                                />
                                Remover
                              </button>
                            </C.Can>
                          </C.PopUpMenu>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </C.Table>
            </div>
          </>
        )}
        <S.PaginationContainer>
          {fornecedores && (
            <C.Pagination
              page={fornecedores?.page}
              total={fornecedores?.total}
              per_page={fornecedores?.per_page}
              last_page={fornecedores?.last_page}
              onSearch={handleSearch}
            />
          )}
        </S.PaginationContainer>
      </S.Panel>

      <C.Modal ref={modalRemoverRef} options={{ width: '400px' }}>
        <C.ModalHeader>
          <C.ModalTitle>Você Confirma a EXCLUSÃO?</C.ModalTitle>
        </C.ModalHeader>
        <C.ModalBody>
          <p>O registro será removido completamente!</p>
        </C.ModalBody>
        <C.ModalFooter>
          <C.Position align="right">
            <C.Button
              variant="outline-secondary"
              onClick={() => modalRemoverRef.current?.closeModal()}
            >
              Cancelar
            </C.Button>
            <C.Button
              variant="primary"
              onClick={() => handleRemove()}
              style={{ marginLeft: '16px' }}
            >
              SIM, confirmo a exclusão
            </C.Button>
          </C.Position>
        </C.ModalFooter>
      </C.Modal>
    </S.Container>
  );
};
